/**
 * The underground's dressing — issue #57's conclusion, applied below `y = 0`.
 *
 * #57 ran four rounds of measurement against the generator — palette, the edge
 * field, the sublattice pitch, ledge clustering — and null'd out every one of
 * them: *"the world's sameness is largely the price of the movement contract."*
 * The owner's decided next step was to stop attacking layout and reach for
 * "everything that is not the shape of the rock": light, weather, inhabitant
 * density and mix. This file is that, mirroring `surfaceDressing.ts`'s own
 * answer to the identical question one clock up — an authored *mood*, not a
 * sampled one, composing an ambient multiplier with how crowded and how mixed
 * the ecology reads.
 *
 * WHY ONE TABLE FOR THE WHOLE UNDERGROUND, NOT ONE PER BIOME. Static per-biome
 * light identity already exists and is fully authored — `BiomeDef.ambientLight`,
 * `palette.glow`, `terrain.glowDensity` (issue #31). The gap #57 asks this file
 * to fill is *temporal* variation, and `surfaceDressing.ts` already answers
 * that question with one table for its whole layer, not one per surface tile.
 * The parallel is exact.
 *
 * WHY NOT A HAZARD CHANNEL. `BiomeHazard.intensity`'s only reader is
 * `worldgen.ts`, at generation time. #57 is explicit that the generator is out
 * of scope for this work — *"the whole point of the decision is that the
 * answer is not in the generator."* So this file touches only run-time reads:
 * the ambient curve and the spawner's already-existing density/mix knobs.
 *
 * Pure in the run seed, unlike `surfaceSeed`. `docs/27-surface.md` §3.1's own
 * clock table says everything below `y = 0` is pure in the run seed and resets
 * every run — a dressing keyed on anything else would contradict the table
 * that already governs this layer.
 */

import { hash2 } from '../core/rng';

export interface UndergroundDressing {
  /** Stable name, for tests and prose. */
  readonly id: string;
  /**
   * Multiplier on the below-ground ambient curve — `lighting.ts` applies it to
   * `ambientAt`'s band values only, never to the daylight/shaft-blend end,
   * which stays exclusively the surface dressing's.
   */
  readonly ambient: number;
  /** Absolute `perChunk` override — how crowded the underground reads. */
  readonly perChunk: number;
  /** Absolute `contamination` override — how often a neighbour bleeds in. */
  readonly contamination: number;
}

/**
 * The authored moods. `'settled'` is a deliberate no-op, byte-identical to
 * today's shipped `EnemySpawner`/`ambientAt` defaults (`DEFAULT_PER_CHUNK`
 * 0.5, `contamination` 0.2, ambient ×1) — one third of the table changes
 * nothing, which bounds the blast radius and gives a clean mutation control.
 */
export const UNDERGROUND_MOODS: readonly UndergroundDressing[] = [
  {
    id: 'hushed',
    ambient: 0.85,
    perChunk: 0.4,
    contamination: 0.12,
  },
  {
    id: 'settled',
    ambient: 1.0,
    perChunk: 0.5,
    contamination: 0.2,
  },
  {
    id: 'restless',
    ambient: 1.1,
    perChunk: 0.6,
    contamination: 0.28,
  },
];

/** The mood this run's seed dresses. Pure; the run owns the clock. */
export function undergroundDressing(runSeed: number): UndergroundDressing {
  return UNDERGROUND_MOODS[hash2(runSeed, 0x9e, 0x3a1) % UNDERGROUND_MOODS.length];
}
